import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { authAPI } from '../services/authAPI'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import { ArrowLeft, Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthShell from '../components/Layout/AuthShell'

const normalizeEmail = (email = '') => email.trim().toLowerCase()

export default function ForgotPassword() {
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    // Pre-fill email if passed from login page
    if (location.state?.email) {
      setEmail(location.state.email)
    }
  }, [location.state])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    
    const normalizedEmail = normalizeEmail(email)
    
    if (!normalizedEmail) {
      setError('Please enter your email address')
      return
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(normalizedEmail)) {
      setError('Please enter a valid email address')
      return
    }
    
    setLoading(true)
    try {
      await authAPI.forgotPassword(normalizedEmail)
      setSuccess(true)
      toast.success('Password reset email sent. Please check your inbox.')
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send password reset email. Please try again.'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      title={success ? 'Check your email' : 'Forgot password?'}
      subtitle={
        success
          ? 'We sent a reset link if that address is registered.'
          : 'Enter your email and we will send a link to reset your password.'
      }
    >
      {success ? (
        <div className="space-y-6">
          <div className="flex gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
            <div>
              <p className="text-sm font-medium text-primary-900">Reset link sent</p>
              <p className="mt-1 text-sm text-primary-700">
                Check <strong>{email}</strong> and follow the instructions. It may take a minute to arrive.
              </p>
            </div>
          </div>
          <p className="text-center text-sm text-secondary-500">
            Did not get it? Check spam, or try again from this page.
          </p>
          <Link to="/login" className="auth-link inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="auth-field">
              Email
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (error) setError(null)
                }}
                className={`auth-input w-full pl-11 ${error ? 'border-error-500' : ''}`}
                disabled={loading}
              />
            </div>
            {error && <p className="mt-2 text-sm text-error-600">{error}</p>}
          </div>

          <Button type="submit" className="h-12 w-full rounded-xl text-[15px] font-semibold" loading={loading} disabled={loading}>
            Send reset link
          </Button>

          <Link to="/login" className="auth-link inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
        </form>
      )}
    </AuthShell>
  )
}
